enum Position {
	Top = 'top',
	TopStart = 'top-start',
	TopEnd = 'top-end',
	Bottom = 'bottom',
	BottomStart = 'bottom-start',
	BottomEnd = 'bottom-end',
	Left = 'left',
	LeftStart = 'left-start',
	LeftEnd = 'left-end',
	Right = 'right',
	RightStart = 'right-start',
	RightEnd = 'right-end',
}

// Gap kept between a horizontally clamped popover and the edge of the viewport.
const VIEWPORT_EDGE = 8;

export default function getPositions(position: Position, boxRect: DOMRect, popoverRect: DOMRect) {
	let top = 0;
	let bottom = 0;
	let left = 0;
	let right = 0;

	// Viewport metrics and not body metrics : boxRect / popoverRect come from
	// getBoundingClientRect and the popover is placed with position fixed, so every number here has
	// to be in the same viewport space. document.body.clientHeight is the content height, which on
	// any page taller than the window threw the bottom / right anchors off screen.
	let bodyHeight = document.documentElement.clientHeight;
	let bodyWidth = document.documentElement.clientWidth;

	if (position.includes('bottom') || position.includes('top')) {
		if (position === 'bottom-start' || position === 'top-start') {
			left = boxRect.x;
		} else if (position === 'bottom' || position === 'top') {
			left = boxRect.x + boxRect.width * 0.5 - popoverRect.width * 0.5;
		} else {
			right = bodyWidth - boxRect.x - boxRect.width;
		}

		// Keep the panel inside the viewport horizontally. An end-anchored panel grows leftwards
		// from the trigger's right edge, so a wide panel on a trigger near the right of a phone
		// screen ran off the left edge (leadzump's notification panel lost its first 50px at
		// 375px). Slide it back in, and move the tip the other way so it still points at the
		// trigger.
		let shift = 0;
		if (position.includes('end')) {
			const maxRight = Math.max(0, bodyWidth - popoverRect.width - VIEWPORT_EDGE);
			if (right > maxRight) {
				shift = right - maxRight;
				right = maxRight;
			}
		} else {
			const maxLeft = Math.max(0, bodyWidth - popoverRect.width - VIEWPORT_EDGE);
			const clamped = Math.min(Math.max(left, Math.min(VIEWPORT_EDGE, maxLeft)), maxLeft);
			shift = clamped - left;
			left = clamped;
		}

		let xAsixTipStyle = position.includes('end')
			? { left: `${boxRect.width * 0.5 + (popoverRect.width - boxRect.width) - shift}px` }
			: position.includes('start')
				? { left: `${boxRect.width * 0.5 - shift}px` }
				: { left: shift ? `calc(50% - ${shift}px)` : `50%` };

		top = boxRect.y + boxRect.height;
		bottom = bodyHeight - boxRect.y;
		const bottomCoords = position.includes('end')
			? { top: top, right: right }
			: { top: top, left: left };
		const topCoords = position.includes('end')
			? { bottom: bottom, right: right }
			: { bottom: bottom, left: left };
		if (top + popoverRect.height > bodyHeight) {
			return {
				coords: topCoords,
				tipPosition: 'bottomTip',
				tipStyle: xAsixTipStyle,
				marginContainer: { marginBottom: '14px' },
			};
		}
		if (bodyHeight - bottom - popoverRect.height < 0) {
			return {
				coords: bottomCoords,
				tipPosition: 'topTip',
				tipStyle: xAsixTipStyle,
				marginContainer: { marginTop: '14px' },
			};
		}
		return position.includes('bottom')
			? {
					coords: bottomCoords,
					tipPosition: 'topTip',
					tipStyle: xAsixTipStyle,
					marginContainer: { marginTop: '14px' },
				}
			: {
					coords: topCoords,
					tipPosition: 'bottomTip',
					tipStyle: xAsixTipStyle,
					marginContainer: { marginBottom: '14px' },
				};
	}

	let yAxisTipStyle = position.includes('end')
		? { top: `${boxRect.height * 0.5 + (popoverRect.height - boxRect.height)}px` }
		: position.includes('start')
			? { top: `${boxRect.height * 0.5}px` }
			: { top: '50%' };

	if (position === 'left-start' || position === 'right-start') {
		top = boxRect.y;
	} else if (position === 'left' || position === 'right') {
		top = boxRect.y + boxRect.height * 0.5 - popoverRect.height * 0.5; ////////
	} else {
		bottom = bodyHeight - boxRect.y - boxRect.height; //////////
	}

	right = bodyWidth - boxRect.x;
	left = boxRect.x + boxRect.width;
	const leftCoords = position.includes('end')
		? { bottom: bottom, right: right }
		: { top: top, right: right };
	const rightCoords = position.includes('end')
		? { bottom: bottom, left: left }
		: { top: top, left: left };
	if (right + popoverRect.width > bodyWidth) {
		return {
			coords: rightCoords,
			tipPosition: 'leftTip',
			tipStyle: yAxisTipStyle,
			marginContainer: { marginLeft: '14px' },
		};
	}
	if (left + popoverRect.width > bodyWidth) {
		return {
			coords: leftCoords,
			tipPosition: 'rightTip',
			tipStyle: yAxisTipStyle,
			marginContainer: { marginRight: '14px' },
		};
	} else {
		return position.includes('left')
			? {
					coords: leftCoords,
					tipPosition: 'rightTip',
					tipStyle: yAxisTipStyle,
					marginContainer: { marginRight: '14px' },
				}
			: {
					coords: rightCoords,
					tipPosition: 'leftTip',
					tipStyle: yAxisTipStyle,
					marginContainer: { marginLeft: '14px' },
				};
	}
}
